/**
 * One of each product per order (T-385). Product pages have no quantity selector and cart lines show a fixed
 * quantity of 1; this keeps the cart at one of each product: a line above 1 goes back to 1, and a second line for a
 * product already in the cart (another variant or selling plan) is removed. It runs on page load, after every cart
 * change, and before checkout (checkout continues only once the cart is in order), then shows the theme setting
 * message ("Cart > One of each product") above the cart items in the cart page and drawer.
 */
(function () {
  var script = document.currentScript;
  var message = (script && script.getAttribute("data-message")) || "";
  var NOTE_KEY = "phasesOneEachNote";
  var routes = (window.theme && window.theme.routes) || {};
  var CART_JS = (routes.root || "/") + "cart.js"; // keeps the storefront's locale prefix, like the theme
  var CART_CHANGE = routes.cart_change_url || "/cart/change";
  var running = null; // the check in progress, if any
  var again = false; // a cart change arrived during a check: run once more after it

  function showNote() {
    if (!message) return;
    document.querySelectorAll("cart-items[data-items-holder]").forEach(function (items) {
      var note = items.previousElementSibling;
      if (!note || !note.hasAttribute("data-phases-one-each-note")) {
        note = document.createElement("p");
        note.className = "phases-one-each-note";
        note.setAttribute("role", "status");
        note.setAttribute("data-phases-one-each-note", "");
        items.parentNode.insertBefore(note, items);
      }
      note.textContent = message;
      note.hidden = false;
    });
  }

  function hideNote() {
    document.querySelectorAll("[data-phases-one-each-note]").forEach(function (note) {
      note.hidden = true;
    });
  }

  // While lines are being corrected the cart's own controls (Remove uses line numbers) wait for the refreshed markup
  function lock(on) {
    document.querySelectorAll("cart-items[data-items-holder]").forEach(function (items) {
      items.classList.toggle("phases-one-each-busy", on);
      if (on) items.setAttribute("aria-busy", "true");
      else items.removeAttribute("aria-busy");
    });
    document.querySelectorAll('button[name="checkout"]').forEach(function (button) {
      if (on) {
        button.setAttribute("data-phases-one-each-locked", "");
        button.setAttribute("aria-disabled", "true");
      } else if (button.hasAttribute("data-phases-one-each-locked")) {
        button.removeAttribute("data-phases-one-each-locked");
        button.removeAttribute("aria-disabled");
      }
    });
  }

  function showError() {
    document.querySelectorAll("cart-items[data-items-holder]").forEach(function (items) {
      var box = items.parentNode.querySelector("[data-phases-one-each-error]");
      if (!box) {
        box = document.createElement("p");
        box.className = "phases-one-each-note phases-one-each-note--error";
        box.setAttribute("role", "alert");
        box.setAttribute("data-phases-one-each-error", "");
        items.parentNode.insertBefore(box, items);
      }
      box.textContent = "Sorry, we couldn't check your cart. Please refresh the page and try again.";
      box.hidden = false;
    });
  }

  function hideError() {
    document.querySelectorAll("[data-phases-one-each-error]").forEach(function (box) {
      box.hidden = true;
    });
  }

  function change(key, quantity) {
    return fetch(CART_CHANGE, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (response) {
      if (!response.ok) throw new Error("Couldn't update the cart (" + response.status + ")");
      return response;
    });
  }

  /** Line changes needed for one of each product: first line of a product → 1, any further lines → removed. */
  function fixesFor(cart) {
    var seen = {};
    var fixes = [];
    ((cart && cart.items) || []).forEach(function (item) {
      if (seen[item.product_id]) {
        fixes.push({ key: item.key, quantity: 0 });
      } else {
        seen[item.product_id] = true;
        if (item.quantity > 1) fixes.push({ key: item.key, quantity: 1 });
      }
    });
    return fixes;
  }

  /** Resolves true when the cart had to be changed, false when it was already one of each. */
  function normalise() {
    return fetch(CART_JS, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("Couldn't read the cart (" + r.status + ")");
        return r.json();
      })
      .then(function (cart) {
        var fixes = fixesFor(cart);
        if (!fixes.length) return false;
        lock(true);
        // one change at a time, removals first so line keys stay valid
        fixes.sort(function (a, b) {
          return a.quantity - b.quantity;
        });
        return fixes
          .reduce(function (chain, fix) {
            return chain.then(function () {
              return change(fix.key, fix.quantity);
            });
          }, Promise.resolve())
          .then(function () {
            return true;
          });
      });
  }

  function refreshCart() {
    // the cart stays locked until it has redrawn (theme:cart:change after the refresh, or the reload)
    try {
      sessionStorage.setItem(NOTE_KEY, "1");
    } catch (e) {}
    if (window.location.pathname === "/cart") {
      window.location.reload();
      return;
    }
    // the drawer redraws itself and fires theme:cart:change again, which shows the note
    document.dispatchEvent(new CustomEvent("theme:cart:refresh", { bubbles: true }));
  }

  // Resolves true if the cart had to change (in this run or a queued re-run), false if it was already one of each,
  // null if it couldn't be checked.
  function check() {
    if (running) {
      again = true;
      return running;
    }
    running = normalise()
      .then(function (changed) {
        hideError();
        if (changed) {
          refreshCart();
          return true;
        }
        lock(false);
        var flagged = false;
        try {
          flagged = sessionStorage.getItem(NOTE_KEY) === "1";
          sessionStorage.removeItem(NOTE_KEY);
        } catch (e) {}
        if (flagged) showNote();
        return false;
      })
      .catch(function (e) {
        console.warn("One of each product:", e && e.message);
        lock(false);
        return null;
      })
      .then(function (result) {
        running = null;
        if (!again) return result;
        again = false;
        // a queued re-run keeps this run's correction or failure: changed or failed wins over "already fine"
        return check().then(function (next) {
          if (result === true || next === true) return true;
          if (result === null || next === null) return null;
          return false;
        });
      });
    return running;
  }

  // Checkout waits for the cart to be one of each: if anything had to change, the cart refreshes and shows the
  // message instead of going to checkout; otherwise the same button is submitted again (so the consent gate and
  // the theme's own handlers still apply).
  var verified = null;
  document.addEventListener(
    "submit",
    function (event) {
      var submitter = event.submitter;
      if (!submitter || submitter.name !== "checkout") return;
      if (verified === submitter) {
        verified = null;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      var form = event.target;
      check().then(function (changed) {
        if (changed === null) showError(); // couldn't check: stay on the cart and say so
        if (changed !== false) return; // changed (refreshing): stay on the cart so the shopper sees it
        verified = submitter;
        if (typeof form.requestSubmit === "function") form.requestSubmit(submitter);
        else submitter.click();
      });
    },
    true,
  );

  document.addEventListener("theme:cart:change", check);
  document.addEventListener("theme:cart:add", function () {
    hideNote();
    check();
  });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", check);
  } else {
    check();
  }
})();
